// The bullet ballet, drawn to be read: pellets are signal red-pink orbs with a white-hot core,
// a dark ink rim (reads on the bright meadow), a soft halo (reads in teal shade) and a tapered
// trail that points back at the shooter. The head is the sim radius up close and never shrinks
// below ~11 px far away. The sniper slug is a long red-hot needle, the washer's bomb a dark ball with a sputtering fuse, the brute's
// boulder a faceted rock. Rewritten from the bullet list every frame (no ring buffer).

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { quadGeometry, hazeUniforms, HAZE_VS, premulMaterial } from './gpu';
import { printMaterial, outlineMaterial, paint, facet } from '../materials';
import { PAL } from '../palette';

export interface BulletRS { id: number; kind: number; x: number; y: number; z: number; vx: number; vy: number; vz: number; age: number }

// sim/bullets.ts BULLETS[].radius, mirrored (const enum values: Pellet 0, Slug 1, Bomb 2, Boulder 3)
const RADIUS = [0.2, 0.12, 0.32, 0.6];

const VS = /* glsl */ `
${HAZE_VS}
attribute vec4 aPos; // xyz, kind
attribute vec4 aVel; // xyz, age
uniform float uTime;
uniform float uPxPerM; // projection scale: screen px per metre at 1 m
varying vec2 vL;     // local coords in body radii: x across, y along (+ = behind)
varying float vKind;
varying float vTail;
varying float vAge;
varying float vHaze;
varying float vSeed;
varying vec3 vHazeCol;
varying float vFore;
varying float vNear;
varying float vVar;
varying float vTiny;
void main() {
  // aPos.w = kind + 16 * variant (variant hashed from the bullet id on the CPU, stable per bolt)
  float vv = floor( aPos.w / 16.0 + 0.001 );
  int kind = int( aPos.w - vv * 16.0 + 0.5 );
  vVar = vv;
  vec3 c = aPos.xyz;
  if ( kind == 3 || kind == 6 ) { gl_Position = vec4( 2.0, 2.0, 2.0, 1.0 ); return; }
  // body radius (the sim radius: what you see is what hits you) and quad extents in radii
  float rb = kind == 0 ? 0.2 : kind == 1 ? 0.12 : kind >= 4 ? 0.2 : 0.11;
  float R = kind == 2 ? 2.4 : 2.8;
  float fullSp = max( length( aVel.xyz ), 1e-3 );
  float tail = kind == 0 ? clamp( fullSp * 0.13, 1.3, 3.2 ) : kind == 1 ? 6.5 : 0.0;
  if ( kind == 2 ) c += vec3( 0.0, 0.42, 0.0 );
  // readability clamp: never smaller than ~10 px across on screen; honest size up close
  float dist = max( length( c - cameraPosition ), 0.5 );
  vNear = smoothstep( 1.2, 3.0, dist ); // a bolt flying through the lens fades instead of filling it
  float px = rb * 2.0 * uPxPerM / dist;
  float minPx = kind == 1 ? 7.0 : 11.0;
  // ...and never bigger than ~26 px across: a bolt passing the lens stays a bolt, not a red blob
  float maxPx = kind == 1 ? 20.0 : 26.0;
  float s = kind == 2 ? 1.0 : ( px < minPx ? min( minPx / max( px, 1e-3 ), 3.2 ) : min( 1.0, maxPx / px ) );
  // far (held at the minimum size) or flying straight at the lens: a small round head whose dark
  // skin reads as a smoke RING at range -- such bolts drop the skin and show a hot dot instead
  vTiny = smoothstep( 1.05, 1.8, s );
  vec3 right = vec3( viewMatrix[0][0], viewMatrix[1][0], viewMatrix[2][0] );
  vec3 up = vec3( viewMatrix[0][1], viewMatrix[1][1], viewMatrix[2][1] );
  vec2 d2 = vec2( dot( aVel.xyz, right ), dot( aVel.xyz, up ) );
  float sp = length( d2 );
  // foreshortened when it flies at you, but never below ~60 %: a bolt must always read as a streak
  float fore = max( sp / fullSp, 0.6 );
  tail *= fore;
  vFore = fore;
  vTiny = max( vTiny, 1.0 - smoothstep( 0.62, 0.85, sp / fullSp ) );
  d2 = sp > 1e-4 ? d2 / sp : vec2( 0.0, 1.0 );
  vec3 fwd = right * d2.x + up * d2.y;
  vec3 sd = right * -d2.y + up * d2.x;
  // grow in over the first frames so a bullet pulled from the muzzle doesn't pop
  float grow = smoothstep( 0.0, 0.08, aVel.w ) * 0.6 + 0.4;
  float tl = tail / ( rb * s ); // tail length in radii (the trail keeps its world length)
  float along = mix( -R, R + tl, position.y + 0.5 );
  float across = position.x * 2.0 * R;
  vec3 wp = c - fwd * along * rb * s * grow + sd * across * rb * s * grow;
  gl_Position = projectionMatrix * viewMatrix * vec4( wp, 1.0 );
  vL = vec2( across, along );
  vKind = float( kind ); vTail = max( tl, 1e-3 ); vAge = aVel.w;
  vSeed = fract( sin( dot( aPos.xyz, vec3( 12.9898, 78.233, 37.719 ) ) - aVel.w ) * 43758.5453 );
  vHaze = hazeAt( c, vHazeCol ) * 0.3;
}
`;

const FS = /* glsl */ `
uniform float uTime;
uniform vec3 uPellet;
uniform vec3 uPelletCore;
uniform vec3 uGlow;
uniform vec3 uSlug;
uniform vec3 uInk;
uniform vec3 uFuse;
varying vec2 vL;
varying float vKind;
varying float vTail;
varying float vAge;
varying float vHaze;
varying float vSeed;
varying vec3 vHazeCol;
varying float vFore;
varying float vNear;
varying float vVar;
varying float vTiny;
float bh( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }
float bn( vec2 p ) {
  vec2 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( bh( i ), bh( i + vec2( 1, 0 ) ), f.x ), mix( bh( i + vec2( 0, 1 ) ), bh( i + vec2( 1, 1 ) ), f.x ), f.y );
}
void main() {
  int kind = int( vKind + 0.5 );
  if ( kind == 4 || kind == 5 ) {
    // disruptor grenades. ice: a faceted pale-cyan crystal ball with a white glint and a frost rim;
    // magnetic: a violet core inside a spinning cyan ring
    vec2 l = vL;
    float r = length( l );
    float spin = vAge * ( kind == 4 ? 3.0 : 9.0 ) + vSeed * 6.283;
    vec2 rl = mat2( cos( spin ), -sin( spin ), sin( spin ), cos( spin ) ) * l;
    vec3 col; float a;
    if ( kind == 4 ) {
      float facet = abs( rl.x ) + abs( rl.y ) * 0.8; // a diamond-cut silhouette
      a = 1.0 - smoothstep( 1.0, 1.08, facet );
      col = mix( vec3( 2.4, 2.9, 3.2 ), vec3( 0.45, 0.78, 1.0 ), smoothstep( 0.0, 0.7, facet ) );
      col = mix( col, vec3( 0.08, 0.2, 0.32 ), smoothstep( 0.86, 1.0, facet ) );
      col += vec3( 1.5 ) * step( 0.82, fract( atan( rl.y, rl.x ) * 0.6366 ) ) * ( 1.0 - smoothstep( 0.3, 0.8, facet ) );
    } else {
      float ring = 1.0 - smoothstep( 0.1, 0.2, abs( length( vec2( rl.x, rl.y * 2.6 ) ) - 1.25 ) );
      float core = 1.0 - smoothstep( 0.62, 0.72, r );
      a = max( core, ring );
      col = core > 0.5 ? mix( vec3( 2.6, 2.2, 3.4 ), vec3( 0.55, 0.3, 1.0 ), smoothstep( 0.0, 0.6, r ) ) : vec3( 0.7, 2.4, 3.0 );
    }
    float glow = exp( -r * r * 0.7 ) * 0.5;
    vec3 gc = kind == 4 ? vec3( 0.35, 0.7, 1.0 ) : vec3( 0.6, 0.35, 1.0 );
    if ( a < 0.01 && glow < 0.01 ) discard;
    gl_FragColor = vec4( col * a + gc * glow * ( 1.0 - a ), a );
    return;
  }
  if ( kind == 2 ) {
    // fuse spark: a sputtering little star on top of the bomb
    vec2 l = vL * 0.11;
    float r = length( l );
    float th = atan( l.y, l.x );
    float fl = 0.7 + 0.3 * sin( vAge * 60.0 ) * sin( vAge * 23.0 );
    float star = mix( 0.06, 0.22, pow( abs( cos( th * 2.5 + vAge * 9.0 ) ), 6.0 ) ) * fl;
    float a = step( r, star );
    float glow = exp( -r * r * 60.0 ) * 0.8;
    vec3 col = mix( uFuse, vec3( 1.0, 0.95, 0.75 ), step( r, 0.06 ) ) * 1.6;
    if ( a < 0.01 && glow < 0.02 ) discard;
    gl_FragColor = vec4( col * a + uFuse * glow * ( 1.0 - a ), 0.0 );
    return;
  }
  vec2 l = vL;
  int vr = kind == 1 ? 0 : int( vVar + 0.5 );
  float pulse = 0.9 + 0.1 * sin( uTime * 11.0 + vSeed * 6.283 );
  // the head is a velocity-stretched TEARDROP: round nose (-y, the way it flies), body tapering
  // into the tail. Three silhouettes keyed to the bolt id: 0 dart, 1 cut lozenge, 2 banded bead.
  float hl = kind == 1 ? 1.4 : mix( 0.7, 2.1, vFore ) * ( vr == 1 ? 1.15 : vr == 2 ? 0.75 : 1.0 );
  float yy = clamp( l.y, -hl * 0.35, hl );
  float taper = mix( 1.0, kind == 1 ? 0.7 : 0.38, smoothstep( -hl * 0.2, hl, l.y ) );
  float wid = ( vr == 2 ? 0.78 : 0.66 ) * taper;
  vec2 q = vec2( l.x / wid, l.y - yy );
  float hr = vr == 1 ? ( abs( q.x ) * 0.82 + abs( q.y ) * 0.82 + length( q ) * 0.18 ) : length( q );
  float ty = clamp( ( l.y - hl ) / max( vTail - hl, 0.3 ), 0.0, 1.0 );
  // printed breakup: a little scrolling value noise so the body and ribbon aren't vector-flat
  float nz = bn( vec2( l.x * 2.2, l.y * 1.1 - vAge * 14.0 ) + vSeed * 37.0 );
  // tapered trail that fades out behind the head, frayed by the noise
  float tw = ( kind == 1 ? 0.5 : vr == 1 ? 0.5 : 0.62 ) * pow( 1.0 - ty, 0.8 ) * 0.9;
  float inTrail = step( 0.0, l.y ) * ( 1.0 - smoothstep( tw - 0.14, tw, abs( l.x ) ) );
  float trailA = inTrail * pow( 1.0 - ty, 1.4 ) * 0.9 * mix( 0.45, 1.0, smoothstep( 0.25, 0.6, nz + ( 1.0 - ty ) * 0.35 ) );
  float head = 1.0 - smoothstep( 0.9, 1.0, hr );
  vec3 body = kind == 1 ? uSlug : uPellet;
  // radial heat ramp: HOT red-pink core (HDR, blooms pink, never white) -> signal red -> crimson skin
  float hrc = hr * ( 1.0 - 0.45 * vTiny ); // a wider hot core when it's only a dot
  vec3 hc = mix( uPelletCore, body * 1.15, smoothstep( 0.1, 0.45, hrc ) );
  hc = mix( hc, body * pulse, smoothstep( 0.4, 0.7, hr ) );
  hc *= mix( 0.78, 1.12, nz ) * ( 1.0 - 0.15 * step( 0.45, hr ) * step( bn( l * 6.0 + vSeed * 11.0 ), 0.3 ) );
  if ( vr == 2 ) hc = mix( hc, uInk * 2.0, ( 1.0 - smoothstep( 0.06, 0.12, abs( l.y - hl * 0.45 ) ) ) * smoothstep( 0.3, 0.5, hr ) );
  hc = mix( hc, uInk * 3.0 + body * 0.3, smoothstep( 0.8, 0.98, hr ) * 0.65 * ( 1.0 - 0.85 * vTiny ) );
  // trail: a hot pink core line fading into the red ribbon
  float tcore = ( 1.0 - smoothstep( 0.15, 0.4, abs( l.x ) / max( tw, 0.05 ) ) ) * ( 1.0 - ty );
  vec3 tc = mix( body * 0.95, uPelletCore * 0.6, tcore * 0.7 );
  float cover = 1.0 - 0.55 * smoothstep( 0.3, 0.8, hr ) + 0.35 * smoothstep( 0.8, 0.97, hr ) * ( 1.0 - vTiny );
  float a = max( head, trailA );
  vec3 col = head > 0.001 ? mix( tc * trailA, hc, head ) / max( a, 1e-3 ) : tc;
  float aOut = max( head * cover, trailA * 0.8 );
  float halo = exp( -hr * hr * 0.8 ) * 0.5 * pulse;
  float trailGlow = step( 0.0, l.y ) * exp( -l.x * l.x * 1.4 ) * ( 1.0 - ty ) * 0.28;
  vec3 glow = uGlow * ( halo + trailGlow ) * ( 1.0 - vHaze );
  col = mix( col, vHazeCol, vHaze );
  if ( a < 0.01 && halo + trailGlow < 0.01 ) discard;
  gl_FragColor = vec4( col * a + glow * ( 1.0 - a ), aOut ) * vNear;
}
`;

const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3(1, 1, 1);
const _ax = new THREE.Vector3();

export class Bullets {
  readonly group = new THREE.Group();
  private orbs: THREE.Mesh;
  private geo: THREE.InstancedBufferGeometry;
  private aPos: THREE.InstancedBufferAttribute;
  private aVel: THREE.InstancedBufferAttribute;
  private bombs: THREE.InstancedMesh;
  private bombInk: THREE.InstancedMesh;
  private rocks: THREE.InstancedMesh;
  private rockInk: THREE.InstancedMesh;
  private r0 = { start: 0, count: 0 };
  private r1 = { start: 0, count: 0 };
  readonly uniforms = { uTime: { value: 0 }, uPxPerM: { value: 600 } };

  constructor(private cap: number) {
    const g = this.geo = quadGeometry(0);
    this.aPos = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
    this.aVel = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4).setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('aPos', this.aPos);
    g.setAttribute('aVel', this.aVel);
    const c = new THREE.Color();
    const v3 = (hex: number, k = 1) => { c.setHex(hex); return new THREE.Vector3(c.r * k, c.g * k, c.b * k); };
    const m = premulMaterial(new THREE.ShaderMaterial({
      vertexShader: VS, fragmentShader: FS,
      uniforms: {
        ...hazeUniforms(), ...this.uniforms,
        // signal red-pink: outside the gold/teal world and distinct from the amber player fire
        uPellet: { value: v3(0xff2e4a, 1.35) }, uPelletCore: { value: v3(0xff8aa0, 3.4) }, uGlow: { value: v3(0xff3a5c, 0.9) },
        uSlug: { value: v3(PAL.laserRed, 1.7) }, uInk: { value: v3(0x2a120e, 1) }, uFuse: { value: v3(PAL.thrusterRing) },
      },
    }));
    m.depthWrite = false;
    this.orbs = new THREE.Mesh(g, m);
    this.orbs.frustumCulled = false;
    this.orbs.renderOrder = 12;

    // washer's bomb: dark iron ball, brass fuse cap
    const ball = paint(facet(new THREE.IcosahedronGeometry(RADIUS[2], 1)), PAL.gunmetal, 0.15, 3);
    const cap_ = paint(facet(new THREE.CylinderGeometry(0.07, 0.09, 0.12, 6).translate(0, RADIUS[2] + 0.03, 0)), PAL.brass);
    const band = paint(facet(new THREE.TorusGeometry(RADIUS[2] * 0.98, 0.035, 3, 10).rotateX(Math.PI / 2)), PAL.washerBand);
    const bombGeo = mergeGeometries([ball, cap_, band])!;
    this.bombs = new THREE.InstancedMesh(bombGeo, printMaterial({ fogMax: 0.3 }), cap >> 2);
    this.bombInk = new THREE.InstancedMesh(bombGeo, outlineMaterial(0.03), cap >> 2);
    this.bombInk.instanceMatrix = this.bombs.instanceMatrix;

    // boulder: a lumpy dodecahedron of island rock
    const rg = new THREE.DodecahedronGeometry(RADIUS[3], 0);
    const pos = rg.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const k = 0.82 + 0.3 * (Math.sin(x * 9.1 + y * 4.3) * 0.5 + 0.5) * (Math.cos(z * 7.7 - x * 3.1) * 0.5 + 0.5);
      pos.setXYZ(i, x * k, y * k * 0.92, z * k);
    }
    const rockGeo = paint(facet(rg), PAL.dirt, 0.18, 7);
    this.rocks = new THREE.InstancedMesh(rockGeo, printMaterial({ fogMax: 0.3 }), cap >> 2);
    this.rockInk = new THREE.InstancedMesh(rockGeo, outlineMaterial(0.04), cap >> 2);
    this.rockInk.instanceMatrix = this.rocks.instanceMatrix;
    for (const im of [this.bombs, this.bombInk, this.rocks, this.rockInk]) {
      im.count = 0; im.frustumCulled = false; im.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    this.group.add(this.orbs, this.bombs, this.bombInk, this.rocks, this.rockInk);
  }

  sync(list: readonly BulletRS[], count: number) {
    const P = this.aPos.array as Float32Array, V = this.aVel.array as Float32Array;
    let n = 0, nb = 0, nr = 0;
    const maxHeavy = this.cap >> 2;
    for (let i = 0; i < count && n < this.cap; i++) {
      const b = list[i];
      const o = n * 4;
      // pellets carry a stable silhouette variant (id hash; the wire has no shooter type)
      P[o] = b.x; P[o + 1] = b.y; P[o + 2] = b.z; P[o + 3] = b.kind + (b.kind === 0 ? ((b.id * 2654435761) >>> 0) % 3 : 0) * 16;
      V[o] = b.vx; V[o + 1] = b.vy; V[o + 2] = b.vz; V[o + 3] = b.age;
      n++;
      if (b.kind === 2 || b.kind === 3 || b.kind === 6) {
        // tumble seeded by id
        const h = Math.sin(b.id * 12.9898) * 43758.5453; const f = h - Math.floor(h);
        _ax.set(Math.cos(f * 6.28), 0.6, Math.sin(f * 6.28)).normalize();
        _q.setFromAxisAngle(_ax, b.age * (b.kind === 2 ? 5 : 2.6) + f * 6);
        _p.set(b.x, b.y, b.z);
        _m.compose(_p, _q, _s);
        if ((b.kind === 2 || b.kind === 6) && nb < maxHeavy) this.bombs.setMatrixAt(nb++, _m);
        else if (b.kind === 3 && nr < maxHeavy) this.rocks.setMatrixAt(nr++, _m);
      }
    }
    this.geo.instanceCount = n;
    this.r0.start = 0; this.r0.count = n * 4; this.r1.start = 0; this.r1.count = n * 4;
    this.aPos.updateRanges.length = 0; this.aPos.updateRanges.push(this.r0); this.aPos.needsUpdate = n > 0;
    this.aVel.updateRanges.length = 0; this.aVel.updateRanges.push(this.r1); this.aVel.needsUpdate = n > 0;
    this.bombs.count = this.bombInk.count = nb;
    this.rocks.count = this.rockInk.count = nr;
    if (nb) this.bombs.instanceMatrix.needsUpdate = true;
    if (nr) this.rocks.instanceMatrix.needsUpdate = true;
    this.orbs.visible = n > 0;
  }

  update(time: number, pxPerM = 600) { this.uniforms.uTime.value = time; this.uniforms.uPxPerM.value = pxPerM; }
}
